import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Save, ExternalLink, Loader, User, Link, Search, Lock, RefreshCw, CheckCircle, AlertTriangle, Mail, Eye } from 'lucide-react';
import { apiClient, ApiError } from '../lib/apiClient';
import { useNotify } from './useNotify';
import { AspirePropertyPicker } from './AspirePropertyPicker';
import { PropertyMap, MapLegend, MapDevice } from './PropertyMap';
import { WaterRestrictionPolicyCard } from './WaterRestrictionPolicyCard';
import { geocodeAddress } from '../lib/googleMaps';
import type { GPSLocation } from '../lib/googleMaps';
import { getUserProfile, isAdmin } from '../lib/auth';
import type { UserRole } from '../lib/auth';

export interface JobSiteFormData {
  name: string;
  address: string;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  gps_location: GPSLocation | null;
  notes: string;
  aspire_property_id: number | null;
}

interface AccountManager {
  name: string | null;
  email: string | null;
  phone: string | null;
}

interface AspirePropertyData {
  PropertyName: string | null;
  PropertyAddress: string | null;
  ContactName: string | null;
  ContactPhone: string | null;
  ContactEmail: string | null;
  Latitude: number | null;
  Longitude: number | null;
}

type RawApiObject = Record<string, unknown>;

interface JobSiteFormModalProps {
  isOpen: boolean;
  /**
   * 'modal' (default) renders as a centered overlay dialog. 'page' renders the
   * same form inline (no backdrop, no max-height) so it can live inside a full-
   * page property detail screen alongside Devices / Inspection History tabs.
   */
  variant?: 'modal' | 'page';
  jobSite?: {
    id: string;
    name: string;
    address: string | null;
    contact_name: string | null;
    contact_phone: string | null;
    contact_email: string | null;
    gps_location: GPSLocation | null;
    notes: string | null;
    aspire_property_id: number | null;
  };
  onClose: () => void;
  onSave: (data: JobSiteFormData) => Promise<string | void>;
  /**
   * View-only mode (Manager property screen). Every field renders as a locked
   * read-out and Save / Cancel / Aspire-relink affordances are removed. It also
   * suppresses the Aspire auto-sync write below — a Manager is not allowed to
   * PUT /api/job-sites, so letting it fire would 403 on every page open.
   */
  readOnly?: boolean;
}

interface ControllerLite {
  id: string;
  job_site_id: string;
  description: string | null;
  location: string | null;
  gps_location: GPSLocation | null;
}
interface WaterMeterLite {
  id: string;
  job_site_id: string;
  meter_number: string | null;
  location: string | null;
  gps_location: GPSLocation | null;
}
interface DoubleCheckLite {
  id: string;
  job_site_id: string;
  description: string | null;
  gps_location: GPSLocation | null;
}
interface ZoneLite {
  id: string;
  controller_id: string;
  zone_number: number;
  location: string | null;
  gps_location: GPSLocation | null;
}

export function JobSiteFormModal({ isOpen, jobSite, onClose, onSave, variant = 'modal', readOnly = false }: JobSiteFormModalProps) {
  const { t } = useTranslation('common');
  const { notify, NotifyModal } = useNotify();
  const [formData, setFormData] = useState<JobSiteFormData>({
    name: '',
    address: '',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    gps_location: null,
    notes: '',
    aspire_property_id: null,
  });
  const [mapDevices, setMapDevices] = useState<MapDevice[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodeWarning, setGeocodeWarning] = useState<string | null>(null);
  const [showAspirePicker, setShowAspirePicker] = useState(false);
  const [aspireConfigured, setAspireConfigured] = useState(false);
  const [accountManager, setAccountManager] = useState<AccountManager | null>(null);
  const [loadingAM, setLoadingAM] = useState(false);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'synced' | 'updated' | 'error'>('idle');
  const [syncUpdatedFields, setSyncUpdatedFields] = useState<string[]>([]);
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [aspirePropertyData, setAspirePropertyData] = useState<AspirePropertyData | null>(null);
  const [rawPropertyData, setRawPropertyData] = useState<RawApiObject | null>(null);
  const [rawAmPropertyData, setRawAmPropertyData] = useState<RawApiObject | null>(null);
  const [rawAmContactData, setRawAmContactData] = useState<RawApiObject | null>(null);
  const originalAddressRef = useRef('');

  useEffect(() => {
    apiClient
      .get<{ configured: boolean }>('/api/integrations/aspire/status')
      .then(({ configured }) => setAspireConfigured(configured))
      .catch(() => setAspireConfigured(false));
    getUserProfile().then((profile) => {
      if (profile) setUserRole(profile.role);
    });
  }, []);

  useEffect(() => {
    if (jobSite) {
      setFormData({
        name: jobSite.name || '',
        address: jobSite.address || '',
        contact_name: jobSite.contact_name || '',
        contact_phone: jobSite.contact_phone || '',
        contact_email: jobSite.contact_email || '',
        gps_location: jobSite.gps_location || null,
        notes: jobSite.notes || '',
        aspire_property_id: jobSite.aspire_property_id || null,
      });
      originalAddressRef.current = jobSite.address || '';
      loadMapDevices(jobSite.id);
    } else {
      setFormData({
        name: '',
        address: '',
        contact_name: '',
        contact_phone: '',
        contact_email: '',
        gps_location: null,
        notes: '',
        aspire_property_id: null,
      });
      originalAddressRef.current = '';
      setMapDevices([]);
      setAccountManager(null);
    }
    setGeocodeWarning(null);
    setSyncStatus('idle');
    setSyncUpdatedFields([]);
    setActiveTooltip(null);
    setAspirePropertyData(null);
    setRawPropertyData(null);
    setRawAmPropertyData(null);
    setRawAmContactData(null);
  }, [jobSite, isOpen]);

  useEffect(() => {
    const aspireId = formData.aspire_property_id;
    if (!aspireId || !aspireConfigured) {
      setAccountManager(null);
      return;
    }

    let cancelled = false;
    const fetchAM = async () => {
      setLoadingAM(true);
      try {
        const data = await apiClient.get<{ account_manager: AccountManager | null }>(
          `/api/integrations/aspire/account-manager?propertyId=${aspireId}`,
        );
        if (cancelled) return;
        if (data.account_manager) setAccountManager(data.account_manager);
      } catch {
        // silent - account manager is optional
      } finally {
        if (!cancelled) setLoadingAM(false);
      }
    };

    fetchAM();
    return () => { cancelled = true; };
  }, [formData.aspire_property_id, aspireConfigured]);

  useEffect(() => {
    if (!jobSite || !jobSite.aspire_property_id || !aspireConfigured || !isOpen) return;
    // View-only viewers cannot write job_sites (Admin-only server-side), so the
    // refresh-from-Aspire pass is skipped entirely rather than 403-ing.
    if (readOnly) return;

    let cancelled = false;
    const syncFromAspire = async () => {
      setSyncStatus('syncing');
      try {
        const data = await apiClient.get<{ property: AspirePropertyData | null }>(
          `/api/integrations/aspire/properties/${jobSite.aspire_property_id}`,
        );
        if (cancelled) return;
        if (!data.property) {
          if (!cancelled) setSyncStatus('error');
          return;
        }

        const aspire = data.property;
        if (!cancelled) setAspirePropertyData(aspire);
        const updates: Record<string, unknown> = {};

        if (aspire.PropertyName && aspire.PropertyName !== jobSite.name) {
          updates.name = aspire.PropertyName;
        }
        if (aspire.PropertyAddress !== (jobSite.address || '')) {
          updates.address = aspire.PropertyAddress || null;
        }
        if (aspire.ContactName !== (jobSite.contact_name || '')) {
          updates.contact_name = aspire.ContactName || null;
        }
        if (aspire.ContactPhone !== (jobSite.contact_phone || '')) {
          updates.contact_phone = aspire.ContactPhone || null;
        }
        if (aspire.ContactEmail !== (jobSite.contact_email || '')) {
          updates.contact_email = aspire.ContactEmail || null;
        }

        const aspireGps = aspire.Latitude != null && aspire.Longitude != null
          ? { latitude: aspire.Latitude, longitude: aspire.Longitude }
          : null;
        const localGps = jobSite.gps_location;
        if (aspireGps && (!localGps || aspireGps.latitude !== localGps.latitude || aspireGps.longitude !== localGps.longitude)) {
          updates.gps_location = aspireGps;
        }

        if (cancelled) return;

        if (Object.keys(updates).length > 0) {
          await apiClient.put(`/api/job-sites/${jobSite.id}`, updates);

          const fieldLabels: string[] = [];
          if (updates.name !== undefined) fieldLabels.push('Property Name');
          if (updates.address !== undefined) fieldLabels.push('Address');
          if (updates.contact_name !== undefined) fieldLabels.push('Contact Name');
          if (updates.contact_phone !== undefined) fieldLabels.push('Contact Phone');
          if (updates.contact_email !== undefined) fieldLabels.push('Contact Email');
          if (updates.gps_location !== undefined) fieldLabels.push('GPS Location');

          setFormData((prev) => ({
            ...prev,
            name: (updates.name as string | undefined) ?? prev.name,
            address: updates.address !== undefined ? ((updates.address as string | null) || '') : prev.address,
            contact_name: updates.contact_name !== undefined ? ((updates.contact_name as string | null) || '') : prev.contact_name,
            contact_phone: updates.contact_phone !== undefined ? ((updates.contact_phone as string | null) || '') : prev.contact_phone,
            contact_email: updates.contact_email !== undefined ? ((updates.contact_email as string | null) || '') : prev.contact_email,
            gps_location: (updates.gps_location as GPSLocation | undefined) ?? prev.gps_location,
          }));
          if (updates.address !== undefined) {
            originalAddressRef.current = (updates.address as string | null) || '';
          }
          if (!cancelled) {
            setSyncUpdatedFields(fieldLabels);
            setSyncStatus('updated');
          }
        } else {
          if (!cancelled) setSyncStatus('synced');
        }
      } catch {
        if (!cancelled) setSyncStatus('error');
      }
    };

    syncFromAspire();
    return () => { cancelled = true; };
  }, [jobSite, aspireConfigured, isOpen, readOnly]);

  const loadMapDevices = async (jobSiteId: string) => {
    try {
      const [allControllers, allMeters, allDoubleChecks, allZones] = await Promise.all([
        apiClient.get<ControllerLite[]>('/api/controllers'),
        apiClient.get<WaterMeterLite[]>('/api/water-meters'),
        apiClient.get<DoubleCheckLite[]>('/api/double-checks'),
        apiClient.get<ZoneLite[]>('/api/zones'),
      ]);
      const siteControllers = allControllers.filter((c) => c.job_site_id === jobSiteId);
      const siteMeters = allMeters.filter((m) => m.job_site_id === jobSiteId);
      const siteDoubleChecks = allDoubleChecks.filter((d) => d.job_site_id === jobSiteId);
      const siteControllerIds = new Set(siteControllers.map((c) => c.id));
      const siteZones = allZones.filter((z) => siteControllerIds.has(z.controller_id));

      const devices: MapDevice[] = [];

      siteControllers.forEach((c) => {
        if (c.gps_location?.latitude && c.gps_location?.longitude) {
          devices.push({
            id: c.id,
            type: 'controller',
            label: c.description || c.location || 'Controller',
            gps_location: c.gps_location,
          });
        }
      });

      siteZones.forEach((z) => {
        if (z.gps_location?.latitude && z.gps_location?.longitude) {
          devices.push({
            id: z.id,
            type: 'zone',
            label: `Zone ${z.zone_number}${z.location ? ` - ${z.location}` : ''}`,
            gps_location: z.gps_location,
          });
        }
      });

      siteMeters.forEach((m) => {
        if (m.gps_location?.latitude && m.gps_location?.longitude) {
          devices.push({
            id: m.id,
            type: 'water_meter',
            label: m.meter_number ? `Meter #${m.meter_number}` : m.location || 'Water Meter',
            gps_location: m.gps_location,
          });
        }
      });

      siteDoubleChecks.forEach((d) => {
        if (d.gps_location?.latitude && d.gps_location?.longitude) {
          devices.push({
            id: d.id,
            type: 'double_check',
            label: d.description || 'Double Check',
            gps_location: d.gps_location,
          });
        }
      });

      setMapDevices(devices);
    } catch (err) {
      console.error('Error loading map devices:', err);
    }
  };

  const handleAddressBlur = async () => {
    if (readOnly) return;
    const address = formData.address.trim();
    if (!address || address === originalAddressRef.current) return;

    setIsGeocoding(true);
    setGeocodeWarning(null);
    try {
      const result = await geocodeAddress(address);
      if (result) {
        setFormData((prev) => ({ ...prev, gps_location: result }));
      }
    } catch {
      // silent on blur - save-time geocode is the safety net
    } finally {
      setIsGeocoding(false);
    }
  };

  const isNewProperty = !jobSite;
  const aspireLinked = !!formData.aspire_property_id;
  const needsAspireSelection = isNewProperty && aspireConfigured && !aspireLinked;
  // A field renders as a locked read-out when Aspire owns it OR the whole
  // screen is view-only.
  const locked = aspireLinked || readOnly;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly) return;

    if (!formData.name.trim()) {
      notify('Notice', 'Please enter a property name', 'warning');
      return;
    }

    if (isNewProperty && aspireConfigured && !formData.aspire_property_id) {
      notify('Notice', 'Please select an Aspire property first', 'warning');
      return;
    }

    setIsSaving(true);
    setGeocodeWarning(null);

    let dataToSave = { ...formData };
    const address = dataToSave.address.trim();
    const addressChanged = address !== originalAddressRef.current;

    if (address && (!dataToSave.gps_location || addressChanged)) {
      try {
        const result = await geocodeAddress(address);
        if (result) {
          dataToSave = { ...dataToSave, gps_location: result };
        } else {
          setGeocodeWarning('Could not resolve GPS coordinates for this address');
        }
      } catch {
        setGeocodeWarning('Could not resolve GPS coordinates for this address');
      }
    }

    try {
      await onSave(dataToSave);
      onClose();
    } catch (error) {
      console.error('Error saving job site:', error);
      if (error instanceof ApiError) {
        // surfaced by parent — nothing extra here
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (variant === 'modal' && !isOpen) return null;

  // unused var suppression — kept for future enrichment of Aspire raw debug paths
  void aspirePropertyData;

  return (
    <div className={variant === 'page' ? 'w-full' : 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'}>
      <div className={variant === 'page' ? 'bg-surface rounded-xl shadow-sm border border-line' : 'bg-surface rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto'}>
        <div className="sticky top-0 bg-surface border-b border-line px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3 min-w-0">
            <h2 className="text-2xl font-bold text-theme-primary truncate">
              {readOnly
                ? t('properties.detailsTitle', 'Property Details')
                : jobSite ? 'Edit Property' : 'Add Property'}
            </h2>
            {readOnly && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-800 dark:bg-blue-500/10 dark:text-blue-300 shrink-0">
                <Eye size={12} />
                {t('properties.viewOnlyBadge', 'View only')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {aspireConfigured && aspireLinked && (
              <div className="flex items-center gap-1.5">
                <div
                  className="relative"
                  onMouseEnter={() => setActiveTooltip('aspire-link')}
                  onMouseLeave={() => setActiveTooltip(null)}
                >
                  <button
                    type="button"
                    onClick={() => setActiveTooltip(activeTooltip === 'aspire-link' ? null : 'aspire-link')}
                    className="p-1.5 rounded-lg bg-teal-50 text-teal-600 hover:bg-teal-100 dark:bg-teal-500/10 dark:text-teal-400 dark:hover:bg-teal-500/20 transition-colors"
                    title={`Linked to Aspire #${formData.aspire_property_id}`}
                  >
                    <Link size={16} />
                  </button>
                  {activeTooltip === 'aspire-link' && (
                    <div className="absolute right-0 top-full pt-1 z-20">
                      <div className={`${isAdmin(userRole) && rawPropertyData ? 'w-80 max-h-96 overflow-y-auto' : 'w-56'} bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg`}>
                        <div className="font-medium mb-1">Aspire Property #{formData.aspire_property_id}</div>
                        <p className="text-gray-300">Name, address, and contact info are managed by Aspire.</p>{/* theme-allow: dark tooltip */}
                        {isAdmin(userRole) && rawPropertyData && (
                          <div className="mt-2 pt-2 border-t border-gray-700 space-y-0.5">{/* theme-allow: dark tooltip */}
                            <div className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-1">Raw Aspire Property</div>{/* theme-allow: dark tooltip */}
                            {Object.entries(rawPropertyData).map(([key, value]) => (
                              <div key={key} className="break-all">
                                <span className="text-gray-400">{key}:</span>{' '}{/* theme-allow: dark tooltip */}
                                <span className="text-gray-200 font-mono">{value === null ? 'null' : typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>{/* theme-allow: dark tooltip */}
                              </div>
                            ))}
                          </div>
                        )}
                        <div className={`flex gap-2 ${readOnly ? '' : 'mt-2'}`}>
                          {!readOnly && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setShowAspirePicker(true); setActiveTooltip(null); }}
                              className="text-teal-300 hover:text-teal-200 underline underline-offset-2"
                            >
                              Change
                            </button>
                          )}
                          {!readOnly && !isNewProperty && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setFormData({ ...formData, aspire_property_id: null });
                                setAccountManager(null);
                                setActiveTooltip(null);
                              }}
                              className="text-red-300 hover:text-red-200 underline underline-offset-2"
                            >
                              Unlink
                            </button>
                          )}
                        </div>
                        <div className="absolute top-0 right-4 w-2 h-2 bg-gray-900 rotate-45" />
                      </div>
                    </div>
                  )}
                </div>

                {syncStatus === 'syncing' && (
                  <div className="p-1.5 rounded-lg bg-blue-50 text-blue-500 dark:bg-blue-500/10">
                    <RefreshCw size={16} className="animate-spin" />
                  </div>
                )}

                {syncStatus === 'synced' && (
                  <div
                    className="relative"
                    onMouseEnter={() => setActiveTooltip('sync-ok')}
                    onMouseLeave={() => setActiveTooltip(null)}
                  >
                    <button
                      type="button"
                      onClick={() => setActiveTooltip(activeTooltip === 'sync-ok' ? null : 'sync-ok')}
                      className="p-1.5 rounded-lg bg-subtle text-theme-secondary hover:bg-subtle transition-colors"
                    >
                      <CheckCircle size={16} />
                    </button>
                    {activeTooltip === 'sync-ok' && (
                      <div className="absolute right-0 top-full pt-1 z-20">
                        <div className="w-48 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg">
                          Up to date with Aspire
                          <div className="absolute top-0 right-4 w-2 h-2 bg-gray-900 rotate-45" />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {syncStatus === 'updated' && (
                  <div
                    className="relative"
                    onMouseEnter={() => setActiveTooltip('sync-updated')}
                    onMouseLeave={() => setActiveTooltip(null)}
                  >
                    <button
                      type="button"
                      onClick={() => setActiveTooltip(activeTooltip === 'sync-updated' ? null : 'sync-updated')}
                      className="p-1.5 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 dark:bg-green-500/10 dark:text-green-400 dark:hover:bg-green-500/20 transition-colors"
                    >
                      <CheckCircle size={16} />
                    </button>
                    {activeTooltip === 'sync-updated' && (
                      <div className="absolute right-0 top-full pt-1 z-20">
                        <div className="w-64 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg">
                          <div className="font-medium mb-1 text-green-300">Updated from Aspire</div>
                          {syncUpdatedFields.length > 0 && (
                            <ul className="text-gray-300 space-y-0.5">{/* theme-allow: dark tooltip */}
                              {syncUpdatedFields.map((f) => (
                                <li key={f} className="flex items-center gap-1">
                                  <span className="w-1 h-1 rounded-full bg-green-400 flex-shrink-0" />
                                  {f}
                                </li>
                              ))}
                            </ul>
                          )}
                          <div className="absolute top-0 right-4 w-2 h-2 bg-gray-900 rotate-45" />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {syncStatus === 'error' && (
                  <div
                    className="relative"
                    onMouseEnter={() => setActiveTooltip('sync-error')}
                    onMouseLeave={() => setActiveTooltip(null)}
                  >
                    <button
                      type="button"
                      onClick={() => setActiveTooltip(activeTooltip === 'sync-error' ? null : 'sync-error')}
                      className="p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 dark:bg-red-500/10 dark:hover:bg-red-500/20 transition-colors"
                    >
                      <AlertTriangle size={16} />
                    </button>
                    {activeTooltip === 'sync-error' && (
                      <div className="absolute right-0 top-full pt-1 z-20">
                        <div className="w-52 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg">
                          Could not sync with Aspire. Data may be out of date.
                          <div className="absolute top-0 right-4 w-2 h-2 bg-gray-900 rotate-45" />
                        </div>
                      </div>
                    )}
                  </div>
                )}

              </div>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-subtle rounded-lg transition-colors"
              disabled={isSaving}
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {needsAspireSelection ? (
          <div className="p-6">
            <div className="text-center py-12 space-y-4">
              <div className="mx-auto w-16 h-16 rounded-full bg-teal-50 dark:bg-teal-500/10 flex items-center justify-center">
                <Search size={28} className="text-teal-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-theme-primary mb-1">Select an Aspire Property</h3>
                <p className="text-sm text-theme-secondary max-w-md mx-auto">
                  All new properties must be imported from Aspire. Search for and select the property you want to add.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowAspirePicker(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors shadow-sm text-sm font-medium"
              >
                <ExternalLink size={16} />
                Search Aspire Properties
              </button>
            </div>
            <div className="flex justify-end pt-6 border-t border-line">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            {aspireConfigured && !aspireLinked && !readOnly && (
              <button
                type="button"
                onClick={() => setShowAspirePicker(true)}
                className="inline-flex items-center gap-1.5 text-sm text-teal-600 hover:text-teal-800 font-medium transition-colors"
              >
                <ExternalLink size={14} />
                Link to Aspire Property
              </button>
            )}

            <div>
              <label className="block text-sm font-medium text-theme-primary mb-2">
                Property Name *
              </label>
              {locked ? (
                <div className="flex items-center gap-2 w-full px-3 py-2 bg-subtle border border-line rounded-lg text-theme-primary">
                  <Lock size={14} className="text-theme-secondary flex-shrink-0" />
                  <span>{formData.name || '--'}</span>
                </div>
              ) : (
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Sunset Valley Apartments"
                  required
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-theme-primary mb-2">
                Address
              </label>
              {locked ? (
                <div className="flex items-center gap-2 w-full px-3 py-2 bg-subtle border border-line rounded-lg text-theme-primary">
                  <Lock size={14} className="text-theme-secondary flex-shrink-0" />
                  <span>{formData.address || '--'}</span>
                </div>
              ) : (
                <div className="relative">
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    onBlur={handleAddressBlur}
                    className="w-full px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., 123 Main Street, Phoenix, AZ 85001"
                  />
                  {isGeocoding && (
                    <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
                      <Loader size={16} className="animate-spin text-blue-500" />
                    </div>
                  )}
                </div>
              )}
              {aspireLinked ? (
                <p className="text-xs text-theme-secondary mt-1">Managed by Aspire</p>
              ) : !readOnly ? (
                <p className="text-xs text-theme-secondary mt-1">GPS coordinates are resolved automatically from the address</p>
              ) : null}
            </div>

            {jobSite?.id && (
              <div className="border-t border-line pt-6">
                <WaterRestrictionPolicyCard jobSiteId={jobSite.id} />
              </div>
            )}

            <div className="border-t border-line pt-6">
              <h3 className="text-lg font-semibold text-theme-primary mb-4">Contact Information</h3>

              {formData.aspire_property_id && (
                <div className="mb-4 p-4 bg-subtle border border-line rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className="relative"
                      onMouseEnter={() => isAdmin(userRole) ? setActiveTooltip('am-aspire-data') : undefined}
                      onMouseLeave={() => setActiveTooltip(null)}
                    >
                      <button
                        type="button"
                        onClick={() => isAdmin(userRole) ? setActiveTooltip(activeTooltip === 'am-aspire-data' ? null : 'am-aspire-data') : undefined}
                        className={`flex items-center gap-2 ${isAdmin(userRole) ? 'cursor-pointer' : 'cursor-default'}`}
                      >
                        <User size={14} className="text-teal-600" />
                        <span className="text-sm font-medium text-theme-primary">Account Manager</span>
                        <span className="text-[10px] uppercase tracking-wider text-teal-600 bg-teal-50 dark:text-teal-400 dark:bg-teal-500/10 px-1.5 py-0.5 rounded font-medium">From Aspire</span>
                      </button>
                      {activeTooltip === 'am-aspire-data' && isAdmin(userRole) && (rawAmPropertyData || rawAmContactData) && (
                        <div className="absolute left-0 top-full pt-1 z-20">
                          <div className="w-80 max-h-96 overflow-y-auto bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg">
                            {rawAmPropertyData && (
                              <div className="space-y-0.5">
                                <div className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-1">Raw Aspire Property</div>{/* theme-allow: dark tooltip */}
                                {Object.entries(rawAmPropertyData).map(([key, value]) => (
                                  <div key={key} className="break-all">
                                    <span className="text-gray-400">{key}:</span>{' '}{/* theme-allow: dark tooltip */}
                                    <span className="text-gray-200 font-mono">{value === null ? 'null' : typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>{/* theme-allow: dark tooltip */}
                                  </div>
                                ))}
                              </div>
                            )}
                            {rawAmContactData && (
                              <div className="mt-2 pt-2 border-t border-gray-700 space-y-0.5">{/* theme-allow: dark tooltip */}
                                <div className="text-[10px] uppercase tracking-wider text-gray-400 font-medium mb-1">Raw Aspire Contact</div>{/* theme-allow: dark tooltip */}
                                {Object.entries(rawAmContactData).map(([key, value]) => (
                                  <div key={key} className="break-all">
                                    <span className="text-gray-400">{key}:</span>{' '}{/* theme-allow: dark tooltip */}
                                    <span className="text-gray-200 font-mono">{value === null ? 'null' : typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>{/* theme-allow: dark tooltip */}
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="absolute top-0 left-6 w-2 h-2 bg-gray-900 rotate-45" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {loadingAM ? (
                    <div className="flex items-center gap-2 text-theme-secondary text-sm">
                      <Loader size={14} className="animate-spin" />
                      Loading...
                    </div>
                  ) : accountManager ? (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                      <div>
                        <span className="text-theme-secondary text-xs">Name</span>
                        <p className="text-theme-primary">{accountManager.name || '--'}</p>
                      </div>
                      <div>
                        <span className="text-theme-secondary text-xs">Email</span>
                        <div className="flex items-center gap-1.5">
                          <p className="text-theme-primary">{accountManager.email || '--'}</p>
                          {accountManager.email && (
                            <a
                              href={`mailto:${accountManager.email}`}
                              className="text-teal-600 hover:text-teal-800 transition-colors flex-shrink-0"
                              title={`Email ${accountManager.email}`}
                            >
                              <Mail size={14} />
                            </a>
                          )}
                        </div>
                      </div>
                      <div>
                        <span className="text-theme-secondary text-xs">Phone</span>
                        <p className="text-theme-primary">{accountManager.phone || '--'}</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-theme-secondary">No account manager assigned in Aspire</p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-theme-primary mb-2">
                    Contact Name
                  </label>
                  {locked ? (
                    <div className="flex items-center gap-2 w-full px-3 py-2 bg-subtle border border-line rounded-lg text-theme-primary text-sm">
                      <Lock size={12} className="text-theme-secondary flex-shrink-0" />
                      <span>{formData.contact_name || '--'}</span>
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      className="w-full px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="John Doe"
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-theme-primary mb-2">
                    Contact Phone
                  </label>
                  {locked ? (
                    <div className="flex items-center gap-2 w-full px-3 py-2 bg-subtle border border-line rounded-lg text-theme-primary text-sm">
                      <Lock size={12} className="text-theme-secondary flex-shrink-0" />
                      <span>{formData.contact_phone || '--'}</span>
                    </div>
                  ) : (
                    <input
                      type="tel"
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      className="w-full px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="(555) 123-4567"
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-theme-primary mb-2">
                    Contact Email
                  </label>
                  {locked ? (
                    <div className="flex items-center gap-2 w-full px-3 py-2 bg-subtle border border-line rounded-lg text-theme-primary text-sm">
                      <Lock size={12} className="text-theme-secondary flex-shrink-0" />
                      <span className="flex-1 truncate">{formData.contact_email || '--'}</span>
                      {formData.contact_email && (
                        <a
                          href={`mailto:${formData.contact_email}`}
                          className="text-teal-600 hover:text-teal-800 transition-colors flex-shrink-0"
                          title={`Email ${formData.contact_email}`}
                        >
                          <Mail size={14} />
                        </a>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <input
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                        className="w-full px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="john@example.com"
                      />
                      {formData.contact_email && (
                        <a
                          href={`mailto:${formData.contact_email}`}
                          className="p-2 text-teal-600 hover:text-teal-800 transition-colors flex-shrink-0"
                          title={`Email ${formData.contact_email}`}
                        >
                          <Mail size={16} />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="border-t border-line pt-6">
              <h3 className="text-lg font-semibold text-theme-primary mb-4">Location</h3>
              <div className="space-y-4">
                {formData.gps_location && (
                  <div className="text-sm text-theme-secondary bg-subtle border border-line rounded-lg px-3 py-2 font-mono">
                    {formData.gps_location.latitude.toFixed(6)}, {formData.gps_location.longitude.toFixed(6)}
                  </div>
                )}

                {!formData.gps_location && formData.address.trim() && (
                  <p className="text-sm text-theme-secondary">GPS coordinates will be resolved when the property is saved.</p>
                )}

                {geocodeWarning && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-300 text-sm">
                    {geocodeWarning}
                  </div>
                )}

                {formData.gps_location && (
                  <div>
                    <PropertyMap
                      propertyLocation={formData.gps_location}
                      propertyName={formData.name || 'Property'}
                      devices={mapDevices}
                      height="360px"
                    />
                    <MapLegend devices={mapDevices} />
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-line pt-6">
              <label className="block text-sm font-medium text-theme-primary mb-2">
                Notes
              </label>
              {readOnly ? (
                <div className="w-full min-h-[6rem] whitespace-pre-wrap px-3 py-2 bg-subtle border border-line rounded-lg text-theme-primary text-sm">
                  {formData.notes || '--'}
                </div>
              ) : (
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                  placeholder="Add any additional notes about this property..."
                />
              )}
            </div>
          </div>

          {readOnly ? (
            <div className="mt-6 pt-6 border-t border-line">
              <p className="text-sm text-theme-secondary">
                {t('properties.viewOnlyHint', 'You have view-only access to this property. Contact an administrator to make changes.')}
              </p>
            </div>
          ) : (
          <div className="flex gap-3 mt-6 pt-6 border-t border-line">
            <button
              type="submit"
              disabled={isSaving}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save size={18} />
              {isSaving ? 'Saving...' : 'Save Property'}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-6 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
          )}
        </form>
        )}
      </div>

      <AspirePropertyPicker
        isOpen={showAspirePicker}
        onClose={() => setShowAspirePicker(false)}
        onSelect={(property) => {
          setFormData({
            ...formData,
            name: property.PropertyName,
            address: property.PropertyAddress,
            contact_name: property.ContactName,
            contact_phone: property.ContactPhone || formData.contact_phone,
            contact_email: property.ContactEmail || formData.contact_email,
            gps_location:
              property.Latitude != null && property.Longitude != null
                ? { latitude: property.Latitude, longitude: property.Longitude }
                : formData.gps_location,
            notes: property.Notes || formData.notes,
            aspire_property_id: property.PropertyID,
          });
        }}
      />

      {NotifyModal}
    </div>
  );
}
